import { SearchOutlined } from '@ant-design/icons';
import { UNIT } from '@const/subsystemMetricsResponse.constants';
import { Status } from '@dto/healthStatus.dto';
import { TableCell } from '@dto/pageResponse.dto';
import { DateUtil } from '@dto/util/DateUtil';
import { UnitUtil } from '@dto/util/UnitUtil';
import { TableSummary } from '@parts/table/TableSummary';
import getPathValue from '@rc-component/util/es/utils/get';
import { GetRef, InputRef, Table, TableProps, Tooltip, TooltipProps } from 'antd';
import { ColumnsType } from "antd/es/table";
import { CompareFn, FilterConfirmProps, SortOrder } from 'antd/es/table/interface';
import { Property } from 'csstype';
import React, { FC, ReactElement, ReactNode, useMemo } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Highlighter from 'react-highlight-words';
import { BsFilter } from 'react-icons/bs';
import { ColProps, FilterType, GridChild, GridProps, GridTableType, IGridEntry, Plain } from "src/component/grid/gridTypes";
import { MeasureCellItem, measureColumnWidths } from 'src/util/measureColumns';
import { TextUtil } from 'src/util/TextUtil';
import { Log } from '../../service/log.service';
import { renderToMeasureHTML } from '../../util/renderStatic';
import { XmDivAnimated } from '../divAnimated/XmDivAnimated';
import './Grid.less';
import XmFilterDropdown from './XmFilterDropDown';
import XmVirtualTable from './XmVirtualTable';

class GridState<T extends IGridEntry> {
  search: Partial<Record<string, {
    term: React.Key;
    contains: boolean;
    isRegex?: boolean;
    isCaseSensitive?: boolean;
    filterFce: (rowVal, record?: IGridEntry, isRegex?: boolean, isCaseSensitive?: boolean) => boolean;
  }>> = {};
  extendedProps: GridProps<T>;
  scrollY?: number | string;
  hasVerticalScroll?: boolean;
  hasHorizontalOverflow?: boolean;
}

function shallowEqualProps(a: object | undefined, b: object | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) return false;
  }
  return true;
}

// Convert width/minWidth values to a number when possible.
function normalizeWidth(value: string | number | undefined): number | string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return value;
  const str = String(value).trim();
  if (str.endsWith('em')) return parseFloat(str) * 12;
  if (str.endsWith('px')) return parseFloat(str);
  // percentages, calc(), etc. can't be safely converted without a parent context;
  // leave them as-is (works in plain mode, may misbehave in virtual mode).
  if (str.endsWith('%') || str.includes('(')) return str;
  const n = parseFloat(str);
  return Number.isFinite(n) ? n : str;
}

function formatValue<RecordType>(value: (string | number) | (string | number)[], col: ColProps<RecordType>,) {
  if (Array.isArray(value))
    value = value.join(";");

  if (value == null)
    return value;

  if (!col.number)
    return col.trim ? col.trimLength ? TextUtil.trimMiddle(value, col.trimLength) : TextUtil.trimMiddle(value) : value;

  const num = Number(value);
  const fractions = col.decimal ?? UnitUtil.getUnitFractionDigits(col.unit);
  switch (col.unit) {
    case UNIT.timestamp:
      return DateUtil.getDateISO(num * 1000);
    case UNIT.percent:
      return value == null ? NaN.toString() : num.toFixed(fractions);
    case UNIT.millisecond:
    case UNIT.cpu_core:
      return num.toFixed(fractions);
    // case UNIT.io_per_second:
    // case UNIT.IOPS:
    // case UNIT.cpu:
    case undefined:
      return num;
    default:
      return (num / col.magnitude).toFixed(fractions);
  }
}

class IData implements Partial<TableCell> {
  order?: number;
  value?: (string | number)[];
  bgStatus?: Status;
  renderProps?: { noClipboard?: boolean; noTooltip?: boolean; };
  noTooltipColumns?: string[];
}

/**
 * Tooltip with default mouse delay
 * @param props
 * @returns
 */
export const TableTooltip: FC<TooltipProps> = (props) => {
  // must keep first value, rerenders on modal close
  const zIndex = useMemo(() => isInsideModalOrDrawer() ? MODAL_TOOLTIP_ZINDEX : TABLE_TOOLTIP_ZINDEX, []);
  return <Tooltip mouseLeaveDelay={0.01} mouseEnterDelay={0.5} zIndex={zIndex} {...props}>{props.children}</Tooltip>;
};
const TABLE_TOOLTIP_ZINDEX = 101;
const MODAL_TOOLTIP_ZINDEX = 9999;

const MODAL_CLASS = '.ant-modal';
const MODAL_DRAWER_CLASS = '.ant-drawer-open';
function isInsideModalOrDrawer() {
  const modals = document.querySelectorAll(MODAL_CLASS);
  const drawers = document.querySelectorAll(MODAL_DRAWER_CLASS);
  if (!modals?.length && !drawers?.length)
    return false;

  for (const modal of modals) {
    const style = getComputedStyle(modal);
    if (style.display !== 'none')
      return true;
  }

  for (const drawer of drawers) {
    const style = getComputedStyle(drawer);
    if (style.display !== 'none')
      return true;
  }

  return false;
}

/**
 * One GridScope for one table
 */
export class GridScope<RecordType extends Record<string, any> & { length?: never; }, RecordEntry extends Plain<RecordType> & IGridEntry = Plain<RecordType> & IGridEntry> {

  static readonly TH_PADDING_WIDTH = 16;
  static readonly TH_PADDING_SORTER = 12;
  static readonly TH_PADDING_FILTER = 15;

  /**
   * Datasource with more rows will render using virtual table
   */
  static readonly ROW_RENDER_LIMIT = 80;

  /**
  * Padding + sorter + filter
  */
  static readonly TH_EXTRA_WIDTH = GridScope.TH_PADDING_WIDTH + GridScope.TH_PADDING_SORTER + GridScope.TH_PADDING_FILTER;

  private tableEl: GetRef<typeof Table>;
  private searchInput: InputRef;
  private data: readonly (RecordEntry)[];
  readonly Grid: new (props: GridProps<RecordType>) => React.Component<GridProps<RecordType>, GridState<RecordEntry>>;

  constructor(private readonly type: GridTableType = "virtual") {
    this.Grid = this.createGridClass(type) as unknown as new (props: GridProps<RecordType>) => React.Component<GridProps<RecordType>, GridState<RecordEntry>>;
  }

  private getState: () => Readonly<GridState<RecordEntry>>;
  private setState: (
    state:
      | ((
        prevState: Readonly<GridState<RecordEntry>>,
        props: Readonly<TableProps<RecordEntry>>
      ) => GridState<RecordEntry> | null)
      | (GridState<RecordEntry> | null),
    callback?: () => void
  ) => void;

  private getColumnSearchProps(props: ColProps<RecordEntry>): ColProps<RecordEntry> {
    const searchProps = this.getState().search[getSearchField()];
    let filterFce = searchProps?.filterFce;
    let contains = searchProps?.contains;

    props.filter.type = props.filter.type ? props.filter.type : 'string';

    const parseNumberFilter = (filter: string) => {
      contains = false;
      if (!filter?.length)
        return () => true;

      const floatRegex = /[+-]?(\d*[.])?\d+/;
      const rangeRegex = new RegExp('^' + floatRegex.source + '-' + floatRegex.source);
      if (/^\D.+/.test(filter)) {
        const value = filter.substring(1);
        const num = Number(value);
        switch (filter.charAt(0)) {
          case '~':
            contains = true;
            return containsFilter(value);
          case '=':
            return (rowVal: number) => rowVal == num;
          case '<':
            return (rowVal: number) => rowVal < num;
          case '>':
            return (rowVal: number) => rowVal > num;
        }
      } else if (rangeRegex.test(filter)) {
        const filterVals = filter.split('-');
        const left = Number(filterVals[0]);
        const right = Number(filterVals[1]);
        return (rowVal: number) => rowVal >= left && rowVal <= right;
      }

      contains = true;
      return containsFilter(filter);
    };

    const getFieldSliced = () => {
      if (props.filter.field[0] === 'data' || props.filter.field[0] === 'rendered')
        return props.filter.field.slice(1);

      return props.filter.field;
    };

    const containsFilter = (filter: string) => (rowVal: number, record: IGridEntry) => {
      let value: number = getPathValue(record.rendered, getFieldSliced());
      value ??= rowVal;

      return value.toString().includes(filter);
    };

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex, type: FilterType, isRegex: boolean, isCaseSensitive: boolean) => {
      switch (type) {
        case 'number':
          filterFce = parseNumberFilter(selectedKeys[0]);
          break;
        case 'timestamp':
          filterFce = (rowVal, record) => rowVal >= selectedKeys[0] && rowVal <= selectedKeys[1];
          break;
        case 'string':
          filterFce = (rowVal: string, record: RecordEntry, isRegex?: boolean, isCaseSensitive?: boolean) => {
            let value = rowVal;
            if (record.rendered) {
              const tmp: string = getPathValue(record.rendered, getFieldSliced());
              if (tmp != null)
                value = tmp;
            }

            if (isRegex) {
              if (props.filter.isTag)
                return value.toString().split(',').some(tag => new RegExp(selectedKeys[0]).test(tag));

              return new RegExp(selectedKeys[0]).test(value.toString());
            }

            if (isCaseSensitive)
              return value.toString().includes(selectedKeys[0]);

            return value.toString().toLowerCase().includes(selectedKeys[0].toLowerCase());
          };

          break;
        default:
          Log.error('Unknown filter type ' + type);
      }

      const state = { ...this.getState() };
      state.search[getSearchField()] = { term: selectedKeys[0], contains, isRegex, isCaseSensitive, filterFce };
      this.setState(state);
      confirm();
    };

    function getSearchField() {
      return props.filter.field.join();
    }

    const handleReset = (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void) => {
      clearFilters();
      const state = { ...this.getState() };
      state.search[getSearchField()] = null;
      this.setState(state);
      confirm();
    };

    return {
      className: ["xm-search", props.className].join(' ').trim(),
      filterDropdown: dropdownProps =>
        <XmFilterDropdown
          {...dropdownProps}
          data={this.data}
          filter={props.filter}
          onSearch={handleSearch}
          onReset={handleReset}
          inputRef={node => {
            this.searchInput = node;
          }}
        />,
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value: string, record: RecordEntry) => {
        if (record.noFilter) return true;
        const { isRegex, isCaseSensitive } = this.getState().search[props.filter.field.join()];
        if (props.onFilter)
          return props.onFilter(value, record, isCaseSensitive, isRegex);

        const val = getPathValue(record, props.filter.field);
        if (val == null)
          return false;

        return filterFce(val, record, isRegex, isCaseSensitive);
      },
      filterDropdownProps: {
        onOpenChange: open => {
          if (open)
            setTimeout(() => this.searchInput?.select(), 100);
        }
      },
      render: (text, record: Plain<RecordEntry>, index) => {
        const getText = () => {
          const rendered = getPathValue(record.rendered, getFieldSliced());
          return (rendered || text) + '';
        };

        const field = props.filter.field.join();

        return props.render ? (
          props.render(text, record, index)
        ) : this.getState().search[field] && (props.filter.type === 'string' || this.getState().search[field].contains) ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[this.getState().search[field].term as string]}
            autoEscape
            textToHighlight={getText()}
          />
        ) : (
          text as ReactNode
        );
      },
    };
  }

  virtualActive = false;
  measuredWidths: Record<string, number> = {};
  private lastMeasureKey: string | null = null;

  getColKey(col: ColProps<RecordEntry>): string {
    if (col.key != null) return String(col.key);
    if (col.dataIndex != null) return ([] as (string | number | symbol)[]).concat(col.dataIndex as never).join('-');
    return '';
  }

  /** Recursively flatten leaf columns from a columns array (skipping group nodes). */
  flattenLeafCols(cols: readonly ColProps<RecordEntry>[] | undefined): ColProps<RecordEntry>[] {
    if (!cols) return [];
    const result: ColProps<RecordEntry>[] = [];
    for (const col of cols) {
      const children = (col as { children?: readonly ColProps<RecordEntry>[]; }).children;
      if (children?.length) result.push(...this.flattenLeafCols(children));
      else result.push(col);
    }
    return result;
  }

  /** Recursively flatten leaf columns from JSX <gs.Col> children. */
  flattenLeafColsFromJSX(children: GridChild<RecordEntry> | GridChild<RecordEntry>[] | undefined): ColProps<RecordEntry>[] {
    if (!children) return [];
    const arr = Array.isArray(children) ? children : [children];
    const result: ColProps<RecordEntry>[] = [];
    for (const child of arr) {
      if (!child) continue;
      const subChildren = (child.props as { children?: GridChild<RecordEntry>[]; }).children;
      if (subChildren?.length) result.push(...this.flattenLeafColsFromJSX(subChildren));
      else result.push(child.props);
    }
    return result;
  }

  /** Column calculation for virtual table. */
  calculateVirtualColumnWidths(cols: ColProps<RecordEntry>[]): void {
    if (!cols.length || !this.data?.length) {
      this.measuredWidths = {};
      this.lastMeasureKey = null;
      return;
    }

    // Skip re-measuring when data and columns haven't changed. componentDidUpdate
    // re-runs init() on any prop reference change (even unrelated parent
    // re-renders), so without this guard every keystroke / hover in a parent
    // re-triggers ReactDOMServer.renderToString for every column.
    const firstRow = this.data[0] as { key?: unknown; };
    const lastRow = this.data[this.data.length - 1] as { key?: unknown; };
    const key = `${this.data.length}|${String(firstRow?.key ?? '')}|${String(lastRow?.key ?? '')}|${cols.map(c => this.getColKey(c)).join(',')}`;
    if (key === this.lastMeasureKey) return;
    this.lastMeasureKey = key;
    this.measuredWidths = {};

    // Step 1: find longest text per column
    interface Longest { text: string; val: unknown; row: RecordEntry; idx: number; }
    const longest: Record<string, Longest> = {};
    const getValue = (row: RecordEntry, col: ColProps<RecordEntry>, colIndex: number): { val: unknown; skip: boolean; } => {
      if (col.dataIndex) {
        const path = (Array.isArray(col.dataIndex) ? col.dataIndex : [col.dataIndex]) as (string | number | symbol)[];
        return { val: getPathValue(row, path), skip: false };
      }
      const rendered = (row as { rendered?: Record<string, { value?: unknown; }>; }).rendered;
      if (col.name && rendered) return { val: rendered[col.name]?.value, skip: false };
      const dataArr = (row as { data?: IData[]; }).data;
      if (Array.isArray(dataArr)) {
        const cell = dataArr[colIndex];
        // Cells flagged with a background status carry no measurable text content.
        if (cell?.bgStatus) return { val: null, skip: true };
        return { val: cell?.value, skip: false };
      }
      return { val: null, skip: true };
    };

    for (let i = 0; i < this.data.length; i++) {
      const row = this.data[i];
      for (let colIdx = 0; colIdx < cols.length; colIdx++) {
        const col = cols[colIdx];
        const key = this.getColKey(col);
        if (!key) continue;
        const { val, skip } = getValue(row, col, colIdx);
        if (skip || val == null) continue;
        let formatted: unknown = formatValue(val as (string | number) | (string | number)[], col);
        if (formatted == null) continue;
        // Object dataIndex (e.g. col.render reads { label, ... } off the value):
        // formatValue returns the object as-is, so .toString() gives "[object Object]".
        // Use the column's render output as the measurable text instead.
        if (typeof formatted === 'object' && typeof col.render === 'function') {
          try {
            const rendered = col.render(val, row, i);
            if (typeof rendered === 'string' || typeof rendered === 'number') formatted = rendered;
          } catch { /* keep formatValue result */ }
        }
        const fullText = String(formatted);
        if (!fullText || fullText === '[object Object]') continue;

        // For multi-line content, the column's natural width is the widest line.
        const candidates = col.newLines ? fullText.split(/\r?\n/) : [fullText];
        for (const candidate of candidates) {
          if (!candidate) continue;
          const prev = longest[key];
          if (!prev || candidate.length > prev.text.length) longest[key] = { text: candidate, val, row, idx: i };
        }
      }
    }

    // Step 2: measure each column's longest content via shared DOM helper.
    const items: MeasureCellItem[] = [];
    for (const col of cols) {
      const key = this.getColKey(col);
      if (!key) continue;
      const item = longest[key];
      if (!item) continue;

      // Prefer the column's rendered output so wrappers (icons, badges) are
      // measured. Components that need React context outside of a render —
      // most notably react-router-dom's <Link> — throw during
      // ReactDOMServer.renderToString. When that happens, fall back to the
      // raw text so the helper measures something meaningful (otherwise the
      // helper renders "[object Object]" and the column ends up ~94px wide
      // regardless of actual content).
      let innerHTML: string | undefined;
      if (typeof col.render === 'function') {
        try {
          const rendered = col.render(item.val, item.row, item.idx);
          if (rendered != null) innerHTML = renderToMeasureHTML(rendered as ReactElement);
        } catch {
          innerHTML = undefined;
        }
      }

      items.push({ key, content: item.text, innerHTML, newLines: col.newLines });
    }

    this.measuredWidths = measureColumnWidths(items);
  }

  private remapCol(cp: ColProps<RecordEntry>, minColumnWidths?: Record<number | string, number>) {
    // Track whether the caller supplied an explicit `width`. If they did
    // (e.g. Table.tsx capping a trim column at COL_MAX_WIDTH), we must not
    // overwrite it later with our own measured-content width. Otherwise the
    // measure-and-grow step below silently overrides Table.tsx's intent and
    // a column meant to stay at 210px ends up at its fullWidth (~265px).
    const callerProvidedWidth = cp.width !== undefined;
    let colProps: ColProps<RecordEntry> = { filterIcon: <BsFilter />, ...cp };
    if (!colProps.minWidth) {
      const titleWidth = TextUtil.measureText(colProps.title as ReactNode, 'min-content') || 0;
      colProps.minWidth = titleWidth + GridScope.TH_PADDING_WIDTH
        + (colProps.filter ? GridScope.TH_PADDING_FILTER : 0)
        + (colProps.sorter ? GridScope.TH_PADDING_SORTER : 0);
    }

    if (colProps.minWidth && cp.orientation !== "vertical") {
      let resolvedMinWidth: Property.MinWidth<string | number> = colProps.minWidth;
      if (this.data && this.data.length > GridScope.ROW_RENDER_LIMIT && minColumnWidths && colProps.dataIndex) {
        const mWidth = colProps.minWidth.toString().includes("em") ? Number.parseInt(colProps.minWidth.toString()) * 12 : Number.parseInt(colProps.minWidth.toString());
        const minColWidth = minColumnWidths[colProps.dataIndex[1]];
        if (minColWidth)
          resolvedMinWidth = mWidth > minColWidth ? colProps.minWidth : minColWidth;
        else if (minColumnWidths[colProps.dataIndex as string | number])
          resolvedMinWidth = mWidth > minColumnWidths[colProps.dataIndex as string | number] ? colProps.minWidth : minColumnWidths[colProps.dataIndex as string | number];
      }

      colProps.minWidth = normalizeWidth(resolvedMinWidth);

      // Ensure every column has an explicit `width`
      if (colProps.width === undefined)
        colProps.width = colProps.minWidth;
    }

    // Normalize an explicit `width`
    if (colProps.width !== undefined)
      colProps.width = normalizeWidth(colProps.width);

    if (typeof colProps.width === 'number' && typeof colProps.minWidth === 'number' && colProps.width < colProps.minWidth)
      colProps.width = colProps.minWidth;

    // Grow width to fit content (both virtual and plain modes). minWidth is
    // the floor; measured content can push the column wider. Only applies
    // when the caller didn't pass an explicit `width` — when they did (e.g.
    // Table.tsx capping a trim column), that intent must win.
    if (!callerProvidedWidth && typeof colProps.width === 'number' && cp.orientation !== "vertical") {
      const measured = this.measuredWidths[this.getColKey(colProps)] ?? 0;
      if (measured > colProps.width) colProps.width = measured;
    }

    // maxWidth caps content-driven growth; an explicit minWidth still wins on conflict.
    if (colProps.maxWidth !== undefined && typeof colProps.width === 'number') {
      const max = normalizeWidth(colProps.maxWidth);
      if (typeof max === 'number' && colProps.width > max)
        colProps.width = typeof colProps.minWidth === 'number' ? Math.max(max, colProps.minWidth) : max;
    }

    if (colProps.filter)
      colProps = { ...colProps, ...this.getColumnSearchProps(colProps) };

    if (colProps.render) {
      if (colProps.plainRender === false || (!colProps.plainRender && !this.getState()?.extendedProps?.renderProps?.noClipboard
        && (colProps.sorter || colProps.onFilter))) {
        const oldRender = colProps.render;
        colProps.render = (value, record, index) => {
          if (typeof value === 'number' || cp.number || value == null)
            return this.colRend(value, record, index, oldRender, cp);

          let txt: string;
          if (Array.isArray(value))
            txt = value.join(', ');
          else if (typeof value === 'string')
            txt = value;
          else
            txt = TextUtil.formatJSON(value);

          return (
            <CopyToClipboard
              text={txt}
              onCopy={() => Log.info(`"${txt}" copied to clipboard.`)}>
              <XmDivAnimated>
                {this.colRend(value, record, index, oldRender, cp)}
              </XmDivAnimated>
            </CopyToClipboard>
          );
        };
      } else {
        const oldRender = colProps.render;
        colProps.render = (value, record, index) => this.colRend(value, record, index, oldRender, cp);
      }
    } else {
      colProps.render = (value, record, index) =>
        this.isTooltip(value, cp.noTooltip) ? <TableTooltip title={value}>
          <div style={{ textAlign: this.getAlign(value, cp) }}>{value}</div></TableTooltip> : value as React.ReactNode;
    }

    // when sorted, empty value always at the end
    if (colProps.sorter && typeof colProps.sorter !== 'boolean' && colProps.dataIndex) {
      function sorterNullLast(originalSorter: CompareFn<RecordEntry>, dataIndex?: typeof colProps.dataIndex) {
        return (a: RecordEntry, b: RecordEntry, sortOrder: SortOrder) => {
          let aValue: unknown, bValue: unknown;
          if (typeof dataIndex === 'string' || typeof dataIndex === 'number') {
            aValue = a[dataIndex];
            bValue = b[dataIndex];
          } else {
            const getRootValue = (obj: RecordEntry) => {
              for (const path of dataIndex as []) {
                if (obj[path] == null)
                  return '';

                obj = obj[path];
              }

              return obj;
            };

            aValue = getRootValue(a);
            bValue = getRootValue(b);
          }

          if (aValue === '' && bValue === '' || aValue === null && bValue === null)
            return 0;

          if (aValue === '' || aValue === null)
            return sortOrder === 'ascend' ? 1 : -1;

          if (bValue === '' || bValue === null)
            return sortOrder === 'ascend' ? -1 : 1;

          return originalSorter(a, b, sortOrder);
        };
      }

      if (typeof colProps.sorter === 'function')
        colProps.sorter = sorterNullLast(colProps.sorter, colProps.dataIndex);

      if (typeof colProps.sorter === 'object')
        colProps.sorter = { ...colProps.sorter, compare: sorterNullLast(colProps.sorter.compare, colProps.dataIndex) };
    }

    return colProps;
  }

  private padGroupChildren(parent: ColProps<RecordEntry>, children: ColProps<RecordEntry>[]): ColProps<RecordEntry>[] {
    if (!children?.length || parent.orientation === 'vertical') return children;

    // A nested group contributes the sum of its own children; a leaf its width.
    const widthOf = (c: ColProps<RecordEntry>): number => {
      const kids = c.children;
      if (kids?.length) return kids.reduce((sum, k) => sum + widthOf(k), 0);
      const w = normalizeWidth(c.width);
      return typeof w === 'number' ? w : 0;
    };

    const parentWidth = (TextUtil.measureText(parent.title as ReactNode, 'min-content') || 0) + GridScope.TH_PADDING_WIDTH;
    const childSum = children.reduce((sum, c) => sum + widthOf(c), 0);
    const deficit = parentWidth - childSum;
    if (deficit <= 0) return children;

    // Distribute the deficit only across flexible value columns — skip helper
    // columns like the color square (className "square-td" / no dataIndex) and
    // nested groups, so those stay at their intended width.
    const isTarget = (c: ColProps<RecordEntry>) =>
      !c.children?.length && c.dataIndex != null && !c.className?.includes('square-td')
      && typeof normalizeWidth(c.width) === 'number';
    const targetCount = children.filter(isTarget).length;
    if (!targetCount) return children;

    const add = Math.ceil(deficit / targetCount);
    return children.map(c => {
      if (!isTarget(c)) return c;
      const w = widthOf(c) + add;
      return { ...c, width: w, minWidth: w };
    });
  }

  private colRend(value: any, record: Plain<RecordEntry>, index: number, render: ColProps<RecordEntry>['render'], cp: ColProps<RecordEntry>) {
    const rendered = render(value, record, index);
    if (rendered?.['type'] === 'meter')
      return rendered as ReactNode;
    if (rendered?.['props']?.tip)
      return rendered as ReactNode;

    let valTip = value;
    if (Array.isArray(value))
      valTip = value.join(', ');
    else if ((typeof value === 'number' || cp.number) && (typeof rendered === 'number' || typeof rendered === 'string'))
      valTip = rendered;

    return (
      <div style={{ textAlign: this.getAlign(value, cp) }}>
        {this.isTooltip(value, cp.noTooltip) ?
          <TableTooltip title={valTip}>
            <div>{rendered as ReactNode}</div>
          </TableTooltip>
          : rendered as ReactNode
        }
      </div>
    );
  }

  private isTooltip(value: string | number | (string | number)[], noTooltip: boolean = false): boolean {
    if (this.getState()?.extendedProps?.renderProps?.noTooltip)
      return false;

    if (noTooltip)
      return false;

    let val = value;
    if (Array.isArray(value))
      if (value.length === 0)
        return false;
      else if (value.length === 1)
        val = value[0];
      else
        val = value.join(', ');

    return (typeof val === 'string' || typeof val === 'number');
  }

  private getAlign(value: string, cp: ColProps<RecordEntry>): Property.TextAlign {
    switch (cp.filter?.type) {
      case 'timestamp':
        return 'inherit';
      case 'number':
        return 'right';
    }

    if (Array.isArray(value) && value.length === 1)
      value = value[0];
    if (typeof value === 'number' && cp.number !== false)
      return 'right';

    return 'inherit';
  }

  private remapChild(cp: GridChild<RecordEntry>) {
    return { ...cp, props: this.remapCol(cp.props) };
  }

  updateRow(row: RecordEntry, data: (RecordEntry)[]) {
    return data.map(r =>
      r.key === row.key ?
        row :
        r
    );
  }

  // Keeping this in case if calculateVirtualColumnWidths does not behave correctly
  // calculateMinWidths(columns: ColProps<RecordEntry>[], data: (RecordEntry)[]) {
  //   const contentColWidths: Record<number | string, number> = {};
  //   if (this.type === "plain" || !columns || !data)
  //     return null;

  //   const w = this.tableEl?.nativeElement?.querySelector('th') || this.tableEl?.nativeElement;
  //   if ((columns.length && columns[0].dataIndex) && !(columns[0].dataIndex instanceof Array)) {
  //     const keys = [];

  //     for (const column of columns)
  //       keys.push(column.dataIndex);

  //     let it = 0;
  //     for (const key of keys as (string | number)[]) {
  //       if (!contentColWidths[key])
  //         contentColWidths[key] = 0;

  //       let largestText = "";
  //       let trimmedText = "";

  //       for (const row of data) {
  //         if (!row[key])
  //           continue;

  //         if (columns[it].newLines) {
  //           const parts = formatValue(row[key], columns[it]).toString().split("\n");
  //           for (const part of parts) {
  //             trimmedText = part;
  //             if (largestText.length < trimmedText.length)
  //               largestText = trimmedText;
  //           }
  //         } else {
  //           trimmedText = formatValue(row[key], columns[it]).toString();
  //           if (largestText.length < trimmedText.length)
  //             largestText = trimmedText;
  //         }

  //         contentColWidths[key] = TextUtil.measureText(largestText as ReactNode, 'min-content',
  //           w) + GridScope.TH_PADDING_WIDTH * 4;
  //       }

  //       it++;
  //     }
  //   } else {
  //     let j = -1;
  //     for (const col of columns) {
  //       if (col.children) {
  //         for (const child of col.children) {
  //           if (this.getColWidth(child, ++j, contentColWidths, data, w) === null)
  //             return null;
  //         }
  //       } else {
  //         if (this.getColWidth(col, ++j, contentColWidths, data, w) === null)
  //           return null;
  //       }
  //     }
  //   }

  //   return contentColWidths;
  // }

  // private getColWidth(col: ColProps<RecordEntry>, index: number, contentColWidths: Record<string | number, number>, data: (RecordEntry)[], refEl: HTMLDivElement) {
  //   let largestText = "";
  //   let trimmedText = "";

  //   if (!contentColWidths[index])
  //     contentColWidths[index] = 0;

  //   for (const row of data) {
  //     let formatted: string;
  //     if (row.rendered) {
  //       const val = row.rendered[col.name]?.value;
  //       if (!val) continue;
  //       formatted = formatValue(val, col)?.toString();
  //     } else {
  //       const tada = row['data'] as IData[];
  //       if (!(tada instanceof Array))
  //         return null;

  //       const datar = tada?.[index]?.value;
  //       if (!datar)
  //         continue;

  //       if (tada[index]?.bgStatus)
  //         continue;

  //       formatted = formatValue(datar, col)?.toString();
  //     }

  //     if (col.newLines) {
  //       const parts = formatted.split("\n");
  //       for (const part of parts) {
  //         trimmedText = part;
  //         if (largestText.length < trimmedText.length)
  //           largestText = trimmedText;
  //       }
  //     } else {
  //       trimmedText = formatted;
  //       if (largestText.length < trimmedText.length)
  //         largestText = trimmedText;
  //     }
  //   }

  //   contentColWidths[index] = TextUtil.measureText(largestText as ReactNode, 'min-content',
  //     refEl) + GridScope.TH_PADDING_WIDTH * 4;
  // }


  /**
   * Syntactic sugar
   * @param _ Column props with filter shorthand
   */
  readonly Col = (_: ColProps<RecordType>) => {
    return null;
  };

  private createGridClass(type: GridTableType) {

    const outer = this;
    return class Grid extends React.Component<GridProps<RecordEntry>, GridState<RecordEntry>> {
      constructor(props: GridProps<RecordEntry>) {
        super(props);
        outer.getState = () => this.state;
        outer.setState = this.setState.bind(this);
        this.state = new GridState<RecordEntry>();
        this.state = { ...this.state, extendedProps: this.init() };
      }

      private readonly tableRef = React.createRef<HTMLDivElement>();
      private bodyResizeObserver: ResizeObserver | null = null;
      private observedBody: HTMLElement | null = null;

      private readonly handleResizeEvent = () => {
        const calculatedY = window.innerHeight - (this.props.topOffset ?? 110);
        this.setState({ scrollY: +this.state?.extendedProps?.scroll?.y ? +this.state.extendedProps.scroll.y : calculatedY > 0 ? calculatedY : "100%" });
      };

      // Determines if a table is really overflowing and vertical scroll should be shown
      private readonly checkVerticalScroll = () => {
        const body = this.tableRef.current?.querySelector<HTMLElement>('.ant-table-body');
        if (!body) {
          if (this.observedBody) {
            this.bodyResizeObserver?.unobserve(this.observedBody);
            this.observedBody = null;
          }
          return;
        }
        // Re-attach observer if the body element changed (e.g. after re-render)
        if (this.observedBody !== body) {
          if (this.observedBody) this.bodyResizeObserver?.unobserve(this.observedBody);
          this.bodyResizeObserver?.observe(body);
          this.observedBody = body;
        }

        const hasVScroll = body.scrollHeight > body.offsetHeight + 1; // 1px epsilon
        // The sticky header only needs syncing when the body actually overflows
        // horizontally. Detecting it from the DOM lets us add scroll.x (and its
        // forced minWidth: 100%) only then, so a table that fits stays sized to
        // its content instead of stretching to fill with an ugly gap.
        const hasHOverflow = body.scrollWidth > body.clientWidth + 1; // 1px epsilon
        if (hasVScroll !== this.state.hasVerticalScroll || hasHOverflow !== this.state.hasHorizontalOverflow)
          this.setState({ hasVerticalScroll: hasVScroll, hasHorizontalOverflow: hasHOverflow });
      };

      private init(): GridProps<RecordEntry> {
        const extProps: GridProps<RecordEntry> = { renderProps: { noClipboard: true, noTooltipColumns: [] }, ...this.props };
        outer.data = this.props.dataSource;
        outer.virtualActive = type === "virtual" && (
          (this.props.dataSource && this.props.dataSource.length > GridScope.ROW_RENDER_LIMIT) ||
          !!this.props.horizontalVirtualization
        );
        extProps.className = ["xm-grid", extProps.className].join(' ').trim();
        // extProps.minColumnWidths = outer.calculateMinWidths(extProps.columns, extProps.dataSource as (RecordEntry)[]);

        // Measure widths for both virtual and plain modes — plain mode tables
        // also need columns to grow past `minWidth` when content is wider,
        // otherwise long values overflow into the next column.
        {
          const leaves = extProps.children
            ? outer.flattenLeafColsFromJSX(extProps.children)
            : outer.flattenLeafCols(extProps.columns);
          outer.calculateVirtualColumnWidths(leaves);
        }

        if (extProps.children) {
          if (!Array.isArray(extProps.children))
            extProps.children = [extProps.children];

          extProps.children = extProps.children.filter(c => c).map(child => {
            if (child.props.children) {
              const kids = child.props.children as GridChild<RecordEntry>[];
              const mapped = outer.padGroupChildren(child.props, kids.map((c) => outer.remapCol(c.props, extProps.minColumnWidths)));
              return {
                ...child,
                props: { ...child.props, children: kids.map((c, i) => ({ ...c, props: mapped[i] })) },
              };
            } else {
              return outer.remapChild(child);
            }
          });
        } else if (extProps.columns) {
          extProps.columns = extProps.columns.map(child => {
            if (child.children) {
              const mapped = child.children.map((c: ColProps<RecordEntry>) => outer.remapCol(c, extProps.minColumnWidths));
              return {
                ...child,
                children: outer.padGroupChildren(child, mapped)
              };
            } else {
              return outer.remapCol(child, extProps.minColumnWidths);
            }
          });
        }

        return extProps;
      }

      shouldComponentUpdate(nextProps: GridProps<RecordEntry>, nextState: GridState<RecordEntry>): boolean {
        return this.state !== nextState || !shallowEqualProps(this.props, nextProps);
      }

      componentDidMount(): void {
        this.handleResizeEvent();
        window.addEventListener('resize', this.handleResizeEvent);
        this.bodyResizeObserver = new ResizeObserver(() => this.checkVerticalScroll());
        requestAnimationFrame(() => this.checkVerticalScroll());
      }

      componentDidUpdate(prevProps: GridProps<RecordEntry>) {
        if (!shallowEqualProps(prevProps, this.props))
          this.setState({ extendedProps: this.init() });
        requestAnimationFrame(() => this.checkVerticalScroll());
      }

      componentWillUnmount(): void {
        window.removeEventListener('resize', this.handleResizeEvent);
        if (this.observedBody) this.bodyResizeObserver?.unobserve(this.observedBody);
        this.bodyResizeObserver?.disconnect();
        this.bodyResizeObserver = null;
        this.observedBody = null;
      }
      render() {
        const useVirtualization = type === "virtual" && (
          (this.props.dataSource && this.props.dataSource.length > GridScope.ROW_RENDER_LIMIT) ||
          this.props.horizontalVirtualization
        );

        const wrapperClass = `xm-grid-wrapper ${this.state.hasVerticalScroll === false ? 'xm-no-vscroll' : ''}`;

        if (useVirtualization) {
          // return <LazyLoadComponent threshold={-100} delayTime={20000} delayMethod='debounce' placeholder={<div style={{ height: 200 }}></div>}>
          //   <Delayed waitBeforeShow={2000} placeholder={<div style={{ height: 200 }}><Spinner /></div>}>
          return (
            <div ref={this.tableRef} className={wrapperClass}>
              <XmVirtualTable<RecordEntry>
                size="small"
                tableLayout={'auto'}
                showSorterTooltip={false}
                pagination={false}
                bordered
                {...this.state.extendedProps}
                {...(this.state.extendedProps?.noScrollY
                  ? {}
                  : this.props.syncHeader
                    ? { scroll: { x: 'max-content', y: this.state.scrollY } }
                    : { scroll: { y: this.state.scrollY } }
                )}
                horizontalVirtualization={this.props.horizontalVirtualization}
                maxVisibleColumns={this.props.maxVisibleColumns}
              />
            </div>
          );

          //   </Delayed>
          // </LazyLoadComponent>;
        }

        const plainScrollX = (!this.state.hasHorizontalOverflow)
          ? undefined
          : (this.state.extendedProps?.scroll?.x ?? 'max-content');

        // return <LazyLoadComponent threshold={-100} delayTime={20000} delayMethod='debounce' placeholder={<div style={{ height: 200 }}></div>}>
        //   <Delayed waitBeforeShow={2000} placeholder={<div style={{ height: 200 }}><Spinner /></div>}>
        return (
          <div ref={this.tableRef} className={wrapperClass}>
            <Table<RecordEntry>
              size="small"
              sticky={true}
              tableLayout={'auto'}
              showSorterTooltip={false}
              pagination={false}
              bordered
              ref={r => outer.tableEl = r}
              summary={this.state.extendedProps.summaryRow ? () => <TableSummary<Plain<RecordType>> data={this.state.extendedProps.summaryRow} cols={this.state.extendedProps.children} /> : null}
              {...this.state.extendedProps}
              {...(this.state.extendedProps?.noScrollY
                ? {}
                : { scroll: { x: plainScrollX, y: this.state.scrollY } }
              )}
              columns={this.state.extendedProps.columns as ColumnsType<RecordEntry>}
            />
          </div>
        );

        //   </Delayed>
        // </LazyLoadComponent>;
      }
    };

  }

}
